import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.scss';
import FileTransfer from './components/FileTransfer/FileTransfer';
import logo from './img/avionica.png';
import Download from './components/Download/Download';
import Serialfetch from './components/Serialunits/serial';
import MasterPortal from './components/MasterPortal/MasterPortal';
import SupportPortal from './components/SupportPortal/SupportPortal';
import DownloadPortal from './components/DownloadPortal/DownlaodPortal';
import Firmware from './components/Firmware/Firmware';
import AppBackground from './common/AppBackground';
import FAQ from './components/FAQ/Faq'

const API_URL = process.env.REACT_APP_API_URL;

const App = () => {
  // Read all URL flags once at mount — these never change during a session
  const params       = new URLSearchParams(window.location.search);
  const [masterMode]   = useState(() => params.has('master'));
  const [supportMode]  = useState(() => params.has('support'));
  const [downloadMode] = useState(() => params.has('download'));

  const [selectedCompany, setSelectedCompany] = useState('');
  const [companyDisplay, setCompanyDisplay]   = useState('');
  const [selectedView, setSelectedView]       = useState(null);
  const [serialNumber, setSerialNumber]       = useState('');
  const [firmwareMode, setFirmwareMode]       = useState(false);
  const [needsSerialPick, setNeedsSerialPick] = useState(false);
  const [companyMap, setCompanyMap]           = useState(null); // null = still loading
  const [instructions, setInstructions]       = useState('');   // which instruction set to render
  const [faqKey, setFaqKey]                   = useState('');   // which FAQ set to render (assigned via company map)

  // Fetch company map on mount — needed by all modes to resolve ?company= param.
  //
  // Two-table model:
  //   companies: [{ companyId, display }]        — the overarching entity: display +
  //                                                 the companyName used for upload
  //                                                 routing and download authorization.
  //   pages:     [{ key, companyId, instructions, faq?, display? }]
  //                                              — one per ?company=<KEY> link. A company
  //                                                 can have many keys; each key picks its
  //                                                 own instruction set and FAQ set and
  //                                                 (optionally) overrides the company's display.
  //
  // Normalized into a KEY -> { companyId, display, instructions, faq } lookup.
  useEffect(() => {
    fetch(`${API_URL}/getCompanyMap`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const map       = {};   // KEY -> { companyId, display, instructions, faq }
        const companies = {};    // companyId -> display

        // companies — the centralized registry: one entry per companyId holding
        // its display. This is the single source of display + data-routing identity.
        (data.companies || []).forEach(c => {
          if (c && c.companyId) companies[c.companyId] = c.display || c.companyId;
        });

        // pages — one per ?company=<KEY> link. Many keys can share a companyId;
        // each key picks its own instruction set. Display comes from the companyId.
        (data.pages || []).forEach(page => {
          if (!page || !page.key) return;
          const companyId = page.companyId || page.key;
          map[page.key.toUpperCase()] = {
            companyId,
            display:      companies[companyId] || companyId,
            instructions: page.instructions || companyId,
            // FAQ set is assignable per-key like instructions; falls back to the
            // companyId so companies without an explicit `faq` behave as before
            // (fetch FAQ/<companyId>.json).
            faq:          page.faq || companyId,
          };
        });

        setCompanyMap(map);
      })
      .catch(err => {
        console.error('Failed to load company map:', err);
        setCompanyMap({});
      });
  }, []);

  // Once companyMap is loaded, resolve company + serial from URL params
  useEffect(() => {
    if (companyMap === null) return;

    const p = new URLSearchParams(window.location.search);

    if (p.has('firmware')) {
      setFirmwareMode(true);
      return;
    }

    const serial   = p.get('serial') || '';
    const rawKey   = (p.get('company') || '').trim();
    const upperKey = rawKey.toUpperCase();

    setSerialNumber(serial);

    if (!rawKey) return;

    // Serial picker is only relevant for the QR upload flow, not download mode
    if (!serial && !downloadMode) setNeedsSerialPick(true);

    const entry = companyMap[upperKey];
    if (entry) {
      setCompanyDisplay(entry.display);
      setSelectedCompany(entry.companyId);
      setInstructions(entry.instructions);
      setFaqKey(entry.faq);
    } else {
      const fallback = rawKey.charAt(0).toUpperCase() + rawKey.slice(1).toLowerCase();
      setCompanyDisplay(fallback);
      setSelectedCompany(rawKey);
      setInstructions(rawKey);
      setFaqKey(rawKey);
    }
  }, [companyMap]);

  // useEffect(() => {
  //   if (!firmwareMode) return;
  //   fetch('https://fdl-service.azurewebsites.net/downloads?companyName=Firmware')
  //     .catch(err => console.error('Error fetching firmware list:', err));
  // }, [firmwareMode]);

  const handleViewSelect = view => setSelectedView(view);

  // ── Master portal ─────────────────────────────────────────────────────────────
  if (masterMode) {
    return (
      <>
        <AppBackground />
        <MasterPortal />
      </>
    );
  }

  // ── Support portal (?support) ─────────────────────────────────────────────────
  if (supportMode) {
    return (
      <>
        <AppBackground />
        <SupportPortal />
      </>
    );
  }

  // ── Download portal (?download&company=X) ─────────────────────────────────────
  // Completely separate render path — never falls through to QR/upload logic.
  // companyMap === null means the fetch is still in flight; show a loading state
  // so we don't flash "Invalid Link" before the company resolves.
  if (downloadMode) {
    // Still waiting for company map fetch
    if (companyMap === null) {
      return (
        <div className="App" style={{ minHeight: '100vh' }}>
          <AppBackground />
          <div className="container d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
            <div className="logo-header mb-5 text-center">
              <img className="avionica-logo" src={logo} alt="Logo" />
              <h1>avCloudConnect</h1>
            </div>
            <p style={{ color: 'var(--muted)' }}>Loading…</p>
          </div>
        </div>
      );
    }

    // Company map loaded but no ?company= param in the URL
    if (!selectedCompany) {
      return (
        <div className="App" style={{ minHeight: '100vh' }}>
          <AppBackground />
          <div className="container d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
            <div className="logo-header mb-5 text-center">
              <img className="avionica-logo" src={logo} alt="Logo" />
              <h1>avCloudConnect</h1>
            </div>
            <div className="invalid-link-message">
              <h1>Invalid Link</h1>
              <h3>Please use a valid download link.</h3>
            </div>
          </div>
        </div>
      );
    }
      return (
      <div className="App" style={{ minHeight: '100vh' }}>
        <AppBackground />
        <div className="container d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
          <div className="logo-header mb-5 text-center">
            <img className="avionica-logo" src={logo} alt="Logo" />
            <h1>avCloudConnect</h1>
          </div>
          <div className="view-frame">
            <DownloadPortal
              companyName={selectedCompany}
              companyDisplay={companyDisplay}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Firmware list view ────────────────────────────────────────────────────────
  if (firmwareMode) {
    return (
      <Firmware/>
    );
  }

  // ── Normal QR / upload mode ───────────────────────────────────────────────────
  return (
    <div className="App" style={{ minHeight: '100vh' }}>
      <AppBackground />
      <div className="container d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>

        <div className="logo-header mb-5 text-center">
          <img className="avionica-logo" src={logo} alt="Logo" />
          <h1>avCloudConnect</h1>
          {selectedCompany && <h2>Welcome {companyDisplay}</h2>}
        </div>

        {/* Serial picker — QR upload flow only */}
        {!serialNumber && selectedCompany && needsSerialPick && (
          <div className="view-frame text-center">
            <h2 style={{ color: '#EDEDEF' }}>Select your device</h2>
            <div className="d-flex flex-wrap gap-3 justify-content-center mt-3">
              <Serialfetch companyName={selectedCompany} />
            </div>
          </div>
        )}

        {/* Invalid link — only shows when map has loaded AND both serial+company are missing */}
        {!serialNumber && !selectedCompany && companyMap !== null && (
          <div className="invalid-link-message">
            <h1>Invalid Link</h1>
            <h3>Please use QR code to access this page</h3>
          </div>
        )}

        {/* View selector — download button intentionally removed from QR flow */}
        {serialNumber && !selectedView && (
          <div className="view-selector d-flex flex-column align-items-center gap-3">
            <div className="d-flex gap-3">
              <Button className="custom-button" onClick={() => handleViewSelect('file-transfer')}>File Upload</Button>
              <Button className="custom-button" onClick={() => handleViewSelect('download')}>File Manager</Button>
            </div>
            <div className="d-flex gap-3">
              <Button className="custom-button custom-button--ghost" onClick={() => handleViewSelect('update')}>Update</Button>
              <Button className="custom-button custom-button--ghost" onClick={() => handleViewSelect('faq')}>FAQ</Button>
            </div>
          </div>
        )}

        {/* Active view */}
        {selectedView && (
          <div className="view-frame">
            <div className="view-toolbar">
              <Button variant="secondary" className="back-btn" onClick={() => setSelectedView(null)}>
                ← Back
              </Button>
            </div>
            {selectedView === 'file-transfer' && (
              <FileTransfer serialNumber={serialNumber} companyName={selectedCompany} instructions={instructions} />
            )}

            {selectedView === 'download' && (
              <DownloadPortal serialNumber={serialNumber} companyName={selectedCompany} />
            )}

            {selectedView === 'faq' && (
              <FAQ serialNumber={serialNumber} companyName={faqKey} />
            )}

            {selectedView === 'update' && (
              <Firmware embedded />
            )}


          </div>
        )}

      </div>
    </div>
  );
};

export default App;
